import { useCallback, useRef, useState } from "react";

const BOTTOM_BREATHING_ROOM_PX = 24;

/**
 * Measures the remaining viewport height below an element's current top
 * offset, so a scroll container can fill exactly the visible space instead
 * of guessing a fixed pixel offset — which drifts whenever anything above it
 * changes size (e.g. a banner mounting). Re-measures on window resize, on
 * body size changes (covers content above appearing/disappearing after the
 * initial render, such as an async-loaded banner), on the element's own size
 * changing (covers a still-animating CSS transition on an ancestor, e.g. a
 * sidebar expanding from a collapsed rail — the very first measurement can
 * land mid-transition, before content above has reflowed to its final size),
 * and on window scroll (covers the element's top offset changing as it
 * scrolls toward a sticky position).
 *
 * Attach the returned callback `ref` to the element whose available height
 * you want to know.
 */
export function useAvailableViewportHeight() {
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const ref = useCallback((element: HTMLElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!element) return;

    const update = () => {
      setAvailableHeight(
        window.innerHeight -
          element.getBoundingClientRect().top -
          BOTTOM_BREATHING_ROOM_PX,
      );
    };
    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    resizeObserver.observe(document.body);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, { passive: true });

    cleanupRef.current = () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update);
    };
  }, []);

  return { ref, availableHeight };
}
